/**
 * Unified audit trail writer for the care app.
 *
 * Emits the same record shape as `functions/utils/audit.js` so client-initiated
 * and server-initiated halves of an operation read as one timeline. Destination
 * follows the scope of what changed:
 *   - patient-scoped  -> tenants/{tid}/patientFieldAuditLog
 *   - tenant config   -> tenants/{tid}/configAuditLog
 *   - global catalogs  -> globalConfigAuditLog
 *
 * The actor is resolved from the signed-in care user, so callers only describe
 * what changed. Audit writes must never fail the operation they describe, so
 * errors are logged and swallowed.
 */
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getEffectiveTenantId, tenantCollection } from './tenantContext';

export const AUDIT_PATIENT_LOG = 'patientFieldAuditLog';
export const AUDIT_TENANT_CONFIG_LOG = 'configAuditLog';
export const AUDIT_GLOBAL_CONFIG_LOG = 'globalConfigAuditLog';

/** Scope determines which log a record lands in. */
export const AUDIT_SCOPE = {
  PATIENT: 'patient',
  TENANT: 'tenant',
  GLOBAL: 'global',
};

/** Firestore rejects `undefined`; Timestamps need a stable serialized form. */
export function serializeAuditValue(v) {
  if (v === undefined || v === null) return null;
  if (typeof v?.toDate === 'function') {
    try {
      return { __ts: v.toDate().toISOString() };
    } catch {
      return String(v);
    }
  }
  if (Array.isArray(v)) return v.map((x) => serializeAuditValue(x));
  if (v && typeof v === 'object' && v.constructor === Object) {
    const o = {};
    Object.keys(v)
      .sort()
      .forEach((k) => {
        o[k] = serializeAuditValue(v[k]);
      });
    return o;
  }
  return v;
}

/** Build `{ field: { old, new } }` for keys present in `updates`. */
export function computeFieldChanges(previous, updates) {
  if (!previous || !updates || typeof updates !== 'object') return {};
  const changes = {};
  for (const key of Object.keys(updates)) {
    if (key === 'id') continue;
    const oldVal = serializeAuditValue(previous[key]);
    const newVal = serializeAuditValue(updates[key]);
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = { old: oldVal, new: newVal };
    }
  }
  return changes;
}

/**
 * Snapshot a document before deleting it, so the audit row can answer
 * "what was removed" rather than just "something was removed".
 */
export function snapshotForDelete(docData) {
  if (!docData) return null;
  const { ...rest } = docData;
  return serializeAuditValue(rest);
}

let cachedActor = null;
let cachedActorUid = null;

async function resolveActor(explicitActor) {
  if (explicitActor?.uid) {
    return {
      uid: explicitActor.uid,
      email: explicitActor.email || null,
      role: explicitActor.role || null,
      displayName: explicitActor.displayName || null,
    };
  }
  const uid = auth?.currentUser?.uid || null;
  if (!uid) return null;
  if (cachedActorUid === uid && cachedActor) return cachedActor;
  let role = null;
  let displayName = auth?.currentUser?.displayName || null;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const d = snap.data() || {};
      role = String(d.role || '').toLowerCase().trim() || null;
      displayName = d.displayName || `${d.firstName || ''} ${d.lastName || ''}`.trim() || displayName;
    }
  } catch {
    // A missing or unreadable profile still yields an attributable uid.
  }
  const actor = {
    uid,
    email: auth?.currentUser?.email || null,
    role,
    displayName,
  };
  cachedActorUid = uid;
  cachedActor = actor;
  return actor;
}

/** Signing out must not leak the previous user's identity into later audit rows. */
export function resetAuditActorCache() {
  cachedActor = null;
  cachedActorUid = null;
}

/**
 * Append one audit record.
 *
 * @param {Object} p
 * @param {string} p.category - functional area, e.g. 'patient_device', 'pathway', 'billing_setup'
 * @param {string} p.action - what happened, e.g. 'device_added', 'delete', 'update'
 * @param {string} [p.entity] - the kind of thing changed, e.g. 'patientDevice'
 * @param {string} [p.entityId] - id of the thing changed
 * @param {string} [p.patientId] - required for AUDIT_SCOPE.PATIENT
 * @param {string} [p.scope] - one of AUDIT_SCOPE; inferred from patientId when omitted
 * @param {Object} [p.changes] - `{ field: { old, new } }`
 * @param {Object} [p.snapshot] - full document, for creates and deletes
 * @param {Object} [p.details] - free-form context (counts, reasons, job ids)
 * @param {Object} [p.actor] - override the resolved actor
 * @param {string} [p.tenantId] - override the resolved tenant
 */
export async function logAuditEvent({
  category,
  action,
  entity = null,
  entityId = null,
  patientId = null,
  scope = null,
  changes = null,
  snapshot = null,
  details = null,
  actor = null,
  tenantId = null,
}) {
  try {
    const resolvedActor = await resolveActor(actor);
    if (!resolvedActor?.uid) return;

    const resolvedScope = scope || (patientId ? AUDIT_SCOPE.PATIENT : AUDIT_SCOPE.TENANT);
    const resolvedTenantId = resolvedScope === AUDIT_SCOPE.GLOBAL
      ? null
      : (tenantId || (await getEffectiveTenantId()));

    if (resolvedScope !== AUDIT_SCOPE.GLOBAL && !resolvedTenantId) return;

    const record = {
      category: category || 'unknown',
      action: action || 'update',
      entity,
      entityId,
      patientId: patientId || null,
      tenantId: resolvedTenantId,
      changes: changes && Object.keys(changes).length > 0 ? serializeAuditValue(changes) : null,
      snapshot: snapshot ? serializeAuditValue(snapshot) : null,
      details: details ? serializeAuditValue(details) : null,
      actorUid: resolvedActor.uid,
      actorEmail: resolvedActor.email,
      actorRole: resolvedActor.role,
      actorDisplayName: resolvedActor.displayName,
      source: 'care-app',
      createdAt: serverTimestamp(),
    };

    let ref;
    if (resolvedScope === AUDIT_SCOPE.GLOBAL) {
      ref = collection(db, AUDIT_GLOBAL_CONFIG_LOG);
    } else if (resolvedScope === AUDIT_SCOPE.PATIENT) {
      ref = tenantCollection(resolvedTenantId, AUDIT_PATIENT_LOG);
    } else {
      ref = tenantCollection(resolvedTenantId, AUDIT_TENANT_CONFIG_LOG);
    }

    await addDoc(ref, record);
  } catch (e) {
    console.warn('logAuditEvent failed', category, action, e?.message || e);
  }
}

/** Convenience wrapper for tenant-scoped configuration changes. */
export function logConfigAudit(p) {
  return logAuditEvent({ ...p, scope: AUDIT_SCOPE.TENANT });
}

/** Convenience wrapper for cross-tenant catalog changes (deviceSetups, rpmSettings, ...). */
export function logGlobalConfigAudit(p) {
  return logAuditEvent({ ...p, scope: AUDIT_SCOPE.GLOBAL });
}
